'use client';

import type { ChartConfig } from '@/components/ui/chart';
import Image from 'next/image';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip } from '@/components/ui/chart';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/** Card shell used for every chart so screenshots share a consistent frame. */
function ChartCard({
  title,
  children,
  legend,
}: {
  title: string;
  children: React.ReactNode;
  legend?: { label: string; color: string }[];
}) {
  return (
    <Card className="border border-border bg-analytics-background-card py-0">
      {/* space-y-0 cancels CardHeader's default space-y-1.5, which pushes the logo below vertical center */}
      <CardHeader className="flex flex-row items-center justify-between space-y-0 border-b px-6 py-4">
        <CardTitle className="text-lg font-medium">{title}</CardTitle>
        <Image src="/wordmark.svg" alt="cubic" width={51} height={15} className="opacity-70" />
      </CardHeader>
      <CardContent className="px-4 pb-6 pt-5 sm:px-6">
        {children}
        {legend && (
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            {legend.map((item) => (
              <div key={item.label} className="flex items-center gap-1.5">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-xs text-muted-foreground">{item.label}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

type Series = { key: string; label: string; color: string };

/** Tooltip for the percentage share charts — shows raw % per series. */
function ShareTooltip({
  active,
  payload,
  series,
}: {
  active?: boolean;
  payload?: Array<{ dataKey: string; payload: Record<string, unknown> }>;
  series: Series[];
}) {
  if (!active || !payload?.length) {
    return null;
  }

  const row = payload[0]?.payload as Record<string, number | string> | undefined;
  if (!row) {
    return null;
  }

  // Order rows by value, highest share first.
  const items = series
    .filter((s) => typeof row[s.key] === 'number')
    .sort((a, b) => (row[b.key] as number) - (row[a.key] as number));

  return (
    <div className="rounded-lg border border-border/50 bg-background px-3 py-2 text-xs shadow-xl">
      <div className="mb-1.5 font-medium">{String(row.label)}</div>
      <div className="grid gap-1">
        {items.map((s) => (
          <div key={s.key} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: s.color }}
              />
              <span className="text-muted-foreground">{s.label}</span>
            </div>
            <span className="font-medium tabular-nums text-foreground">
              {`${row[s.key] as number}%`}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function buildConfig(series: Series[]): ChartConfig {
  return Object.fromEntries(
    series.map((s) => [s.key, { label: s.label, color: s.color }]),
  ) satisfies ChartConfig;
}

// ---------------------------------------------------------------------------
// 1. Model vendor market share — 100% stacked area, fractional dev-weight (from Mar 9)
// Source: Appendix B.3. Each dev contributes weight 1, split evenly across the
// vendors they used that week → rows sum to ~100%.
// ---------------------------------------------------------------------------

const VENDOR_SERIES: Series[] = [
  { key: 'anthropic', label: 'Anthropic', color: 'hsl(25 90% 55%)' },
  { key: 'openai', label: 'OpenAI', color: 'hsl(160 70% 40%)' },
  { key: 'cursor', label: 'Cursor', color: 'hsl(220 75% 58%)' },
  { key: 'other', label: 'Other', color: 'hsl(240 5% 55%)' },
];

const VENDOR_DATA = [
  { label: 'Mar 9', anthropic: 72.4, openai: 10.5, cursor: 1.3, other: 15.8 },
  { label: 'Mar 16', anthropic: 73.7, openai: 7.9, cursor: 3.7, other: 14.8 },
  { label: 'Mar 23', anthropic: 74.8, openai: 9.2, cursor: 3.4, other: 12.6 },
  { label: 'Mar 30', anthropic: 72.0, openai: 11.0, cursor: 3.0, other: 14.0 },
  { label: 'Apr 6', anthropic: 70.3, openai: 11.9, cursor: 3.1, other: 14.7 },
  { label: 'Apr 13', anthropic: 71.6, openai: 11.4, cursor: 2.0, other: 15.0 },
  { label: 'Apr 20', anthropic: 72.7, openai: 11.8, cursor: 1.8, other: 13.6 },
  { label: 'Apr 27', anthropic: 72.2, openai: 15.1, cursor: 1.3, other: 11.5 },
  { label: 'May 4', anthropic: 70.4, openai: 15.8, cursor: 1.3, other: 12.6 },
  { label: 'May 11', anthropic: 68.4, openai: 19.1, cursor: 1.4, other: 11.1 },
  { label: 'May 18', anthropic: 73.9, openai: 14.0, cursor: 4.9, other: 7.2 },
  { label: 'May 25', anthropic: 76.8, openai: 13.4, cursor: 3.0, other: 6.9 },
  { label: 'Jun 1', anthropic: 76.3, openai: 15.0, cursor: 3.0, other: 5.6 },
  { label: 'Jun 8', anthropic: 77.9, openai: 12.5, cursor: 2.3, other: 7.3 },
  { label: 'Jun 15', anthropic: 79.2, openai: 13.4, cursor: 1.7, other: 5.8 },
];

export function VendorShareChart() {
  return (
    <ChartCard
      title="Model vendor market share"
      legend={VENDOR_SERIES.map((s) => ({ label: s.label, color: s.color }))}
    >
      <ChartContainer config={buildConfig(VENDOR_SERIES)} className="h-72 w-full">
        <AreaChart
          data={VENDOR_DATA}
          stackOffset="expand"
          margin={{ top: 8, right: 20, left: 4, bottom: 0 }}
        >
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={40}
            tickFormatter={(v: number) => `${Math.round(v * 100)}%`}
          />
          <ChartTooltip content={<ShareTooltip series={VENDOR_SERIES} />} />
          {VENDOR_SERIES.map((s) => (
            <Area
              key={s.key}
              dataKey={s.key}
              stackId="vendor"
              type="monotone"
              stroke={s.color}
              fill={s.color}
              fillOpacity={0.85}
              strokeWidth={1}
              isAnimationActive={false}
            />
          ))}
        </AreaChart>
      </ChartContainer>
    </ChartCard>
  );
}

// ---------------------------------------------------------------------------
// 2. Model market share — 100% stacked area, fractional dev-weight (from Mar 9)
// Source: Appendix B.1. Each dev contributes weight 1, split evenly across the
// model families they used that week → rows sum to ~100%.
// ---------------------------------------------------------------------------

// Stack order (bottom → top): Opus wave together, then Sonnet/Fable, then GPT, Composer, Other.
const MODEL_SERIES: Series[] = [
  { key: 'opus46', label: 'Opus 4.6', color: 'hsl(28 55% 68%)' },
  { key: 'opus47', label: 'Opus 4.7', color: 'hsl(25 85% 55%)' },
  { key: 'opus48', label: 'Opus 4.8', color: 'hsl(14 85% 47%)' },
  { key: 'sonnet46', label: 'Sonnet 4.6', color: 'hsl(43 80% 58%)' },
  { key: 'fable5', label: 'Fable 5', color: 'hsl(280 60% 62%)' },
  { key: 'gpt55', label: 'GPT-5.5', color: 'hsl(160 70% 38%)' },
  { key: 'gpt54', label: 'GPT-5.4', color: 'hsl(160 45% 58%)' },
  { key: 'composer', label: 'Composer', color: 'hsl(220 75% 58%)' },
  { key: 'other', label: 'Other', color: 'hsl(240 5% 55%)' },
];

const MODEL_DATA = [
  {
    label: 'Mar 9',
    opus46: 55.6,
    opus47: 0.3,
    opus48: 0,
    sonnet46: 14.3,
    fable5: 0,
    gpt55: 0.8,
    gpt54: 6.1,
    composer: 1.4,
    other: 21.6,
  },
  {
    label: 'Mar 16',
    opus46: 57.6,
    opus47: 0.6,
    opus48: 0,
    sonnet46: 13.9,
    fable5: 0,
    gpt55: 0.3,
    gpt54: 5.8,
    composer: 3.5,
    other: 18.2,
  },
  {
    label: 'Mar 23',
    opus46: 53.1,
    opus47: 0.8,
    opus48: 0,
    sonnet46: 18.5,
    fable5: 0,
    gpt55: 0,
    gpt54: 7.0,
    composer: 3.2,
    other: 17.5,
  },
  {
    label: 'Mar 30',
    opus46: 57.6,
    opus47: 0.1,
    opus48: 0,
    sonnet46: 13.3,
    fable5: 0,
    gpt55: 0.1,
    gpt54: 9.0,
    composer: 3.0,
    other: 16.8,
  },
  {
    label: 'Apr 6',
    opus46: 57.0,
    opus47: 0.8,
    opus48: 0.1,
    sonnet46: 12.2,
    fable5: 0,
    gpt55: 0.1,
    gpt54: 10.0,
    composer: 2.9,
    other: 16.8,
  },
  {
    label: 'Apr 13',
    opus46: 39.9,
    opus47: 19.7,
    opus48: 0,
    sonnet46: 15.5,
    fable5: 0,
    gpt55: 0.8,
    gpt54: 8.1,
    composer: 1.8,
    other: 14.4,
  },
  {
    label: 'Apr 20',
    opus46: 17.1,
    opus47: 42.3,
    opus48: 0,
    sonnet46: 13.5,
    fable5: 0.3,
    gpt55: 3.2,
    gpt54: 7.7,
    composer: 1.6,
    other: 14.2,
  },
  {
    label: 'Apr 27',
    opus46: 10.8,
    opus47: 47.2,
    opus48: 0,
    sonnet46: 14.7,
    fable5: 0.1,
    gpt55: 11.8,
    gpt54: 2.9,
    composer: 1.0,
    other: 11.5,
  },
  {
    label: 'May 4',
    opus46: 7.2,
    opus47: 49.8,
    opus48: 0.1,
    sonnet46: 14.1,
    fable5: 0.3,
    gpt55: 13.8,
    gpt54: 1.1,
    composer: 1.1,
    other: 12.4,
  },
  {
    label: 'May 11',
    opus46: 6.4,
    opus47: 48.8,
    opus48: 0.3,
    sonnet46: 13.5,
    fable5: 0,
    gpt55: 16.8,
    gpt54: 1.6,
    composer: 1.3,
    other: 11.3,
  },
  {
    label: 'May 18',
    opus46: 7.1,
    opus47: 54.7,
    opus48: 0.9,
    sonnet46: 11.0,
    fable5: 0,
    gpt55: 12.1,
    gpt54: 1.4,
    composer: 4.6,
    other: 8.1,
  },
  {
    label: 'May 25',
    opus46: 5.1,
    opus47: 41.5,
    opus48: 19.3,
    sonnet46: 12.9,
    fable5: 0.1,
    gpt55: 11.2,
    gpt54: 0.6,
    composer: 2.7,
    other: 6.6,
  },
  {
    label: 'Jun 1',
    opus46: 3.6,
    opus47: 18.0,
    opus48: 45.7,
    sonnet46: 10.1,
    fable5: 0.3,
    gpt55: 13.0,
    gpt54: 0.6,
    composer: 2.8,
    other: 5.9,
  },
  {
    label: 'Jun 8',
    opus46: 2.7,
    opus47: 6.8,
    opus48: 43.4,
    sonnet46: 12.1,
    fable5: 15.3,
    gpt55: 10.6,
    gpt54: 0.5,
    composer: 1.9,
    other: 6.6,
  },
  {
    label: 'Jun 15',
    opus46: 1.4,
    opus47: 5.1,
    opus48: 60.0,
    sonnet46: 10.7,
    fable5: 2.3,
    gpt55: 12.4,
    gpt54: 0.7,
    composer: 1.8,
    other: 5.6,
  },
];

export function ModelShareChart() {
  return (
    <ChartCard
      title="Model market share"
      legend={MODEL_SERIES.map((s) => ({ label: s.label, color: s.color }))}
    >
      <ChartContainer config={buildConfig(MODEL_SERIES)} className="h-72 w-full">
        <AreaChart
          data={MODEL_DATA}
          stackOffset="expand"
          margin={{ top: 8, right: 20, left: 4, bottom: 0 }}
        >
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={40}
            tickFormatter={(v: number) => `${Math.round(v * 100)}%`}
          />
          <ChartTooltip content={<ShareTooltip series={MODEL_SERIES} />} />
          {MODEL_SERIES.map((s) => (
            <Area
              key={s.key}
              dataKey={s.key}
              stackId="model"
              type="monotone"
              stroke={s.color}
              fill={s.color}
              fillOpacity={0.85}
              strokeWidth={1}
              isAnimationActive={false}
            />
          ))}
        </AreaChart>
      </ChartContainer>
    </ChartCard>
  );
}

// ---------------------------------------------------------------------------
// 3. Coding agent market share — 100% stacked area, fractional dev-weight (from Mar 9)
// Source: Appendix B.2. Each dev contributes weight 1, split evenly across the
// tools they used that week → rows sum to ~100% (no double-counting).
// ---------------------------------------------------------------------------

// Stack order (bottom → top): Claude Code anchors, then by descending share.
const AGENT_SERIES: Series[] = [
  { key: 'claudeCode', label: 'Claude Code', color: 'hsl(25 90% 55%)' },
  { key: 'cursor', label: 'Cursor', color: 'hsl(170 65% 45%)' },
  { key: 'codex', label: 'Codex', color: 'hsl(220 75% 58%)' },
  { key: 'opencode', label: 'opencode', color: 'hsl(52 85% 50%)' },
  { key: 'copilot', label: 'GitHub Copilot', color: 'hsl(130 50% 45%)' },
  { key: 'pi', label: 'pi', color: 'hsl(280 60% 62%)' },
  { key: 'other', label: 'Other', color: 'hsl(240 5% 55%)' },
];

const AGENT_DATA = [
  {
    label: 'Mar 9',
    claudeCode: 69.1,
    cursor: 15.4,
    codex: 8.5,
    opencode: 4.1,
    copilot: 1.2,
    pi: 0,
    other: 1.7,
  },
  {
    label: 'Mar 16',
    claudeCode: 72.1,
    cursor: 18.8,
    codex: 4.9,
    opencode: 2.0,
    copilot: 2.1,
    pi: 0,
    other: 0,
  },
  {
    label: 'Mar 23',
    claudeCode: 70.5,
    cursor: 19.8,
    codex: 4.8,
    opencode: 2.8,
    copilot: 0.9,
    pi: 0,
    other: 1.2,
  },
  {
    label: 'Mar 30',
    claudeCode: 68.4,
    cursor: 22.3,
    codex: 7.0,
    opencode: 1.0,
    copilot: 1.0,
    pi: 0,
    other: 0.3,
  },
  {
    label: 'Apr 6',
    claudeCode: 69.0,
    cursor: 20.1,
    codex: 8.0,
    opencode: 2.1,
    copilot: 0.4,
    pi: 0,
    other: 0.4,
  },
  {
    label: 'Apr 13',
    claudeCode: 69.7,
    cursor: 16.9,
    codex: 9.0,
    opencode: 2.1,
    copilot: 1.4,
    pi: 0.1,
    other: 0.9,
  },
  {
    label: 'Apr 20',
    claudeCode: 70.7,
    cursor: 17.6,
    codex: 8.0,
    opencode: 1.7,
    copilot: 1.2,
    pi: 0.2,
    other: 0.6,
  },
  {
    label: 'Apr 27',
    claudeCode: 69.4,
    cursor: 17.5,
    codex: 9.8,
    opencode: 0.7,
    copilot: 1.4,
    pi: 0.4,
    other: 0.8,
  },
  {
    label: 'May 4',
    claudeCode: 67.3,
    cursor: 17.8,
    codex: 11.6,
    opencode: 0.6,
    copilot: 2.1,
    pi: 0.1,
    other: 0.4,
  },
  {
    label: 'May 11',
    claudeCode: 66.2,
    cursor: 18.9,
    codex: 11.0,
    opencode: 0.8,
    copilot: 0.9,
    pi: 0.1,
    other: 2.1,
  },
  {
    label: 'May 18',
    claudeCode: 70.5,
    cursor: 13.6,
    codex: 11.4,
    opencode: 0.8,
    copilot: 1.5,
    pi: 0.3,
    other: 1.9,
  },
  {
    label: 'May 25',
    claudeCode: 70.9,
    cursor: 13.9,
    codex: 9.6,
    opencode: 0.7,
    copilot: 2.3,
    pi: 1.1,
    other: 1.5,
  },
  {
    label: 'Jun 1',
    claudeCode: 72.0,
    cursor: 13.8,
    codex: 10.2,
    opencode: 0.7,
    copilot: 1.4,
    pi: 1.2,
    other: 0.8,
  },
  {
    label: 'Jun 8',
    claudeCode: 74.4,
    cursor: 12.2,
    codex: 9.8,
    opencode: 1.0,
    copilot: 1.0,
    pi: 0.8,
    other: 0.7,
  },
  {
    label: 'Jun 15',
    claudeCode: 75.1,
    cursor: 11.4,
    codex: 9.7,
    opencode: 1.4,
    copilot: 0.5,
    pi: 1.2,
    other: 0.7,
  },
];

export function AgentShareChart() {
  return (
    <ChartCard
      title="Coding agent market share"
      legend={AGENT_SERIES.map((s) => ({ label: s.label, color: s.color }))}
    >
      <ChartContainer config={buildConfig(AGENT_SERIES)} className="h-72 w-full">
        <AreaChart
          data={AGENT_DATA}
          stackOffset="expand"
          margin={{ top: 8, right: 20, left: 4, bottom: 0 }}
        >
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={40}
            tickFormatter={(v: number) => `${Math.round(v * 100)}%`}
          />
          <ChartTooltip content={<ShareTooltip series={AGENT_SERIES} />} />
          {AGENT_SERIES.map((s) => (
            <Area
              key={s.key}
              dataKey={s.key}
              stackId="agent"
              type="monotone"
              stroke={s.color}
              fill={s.color}
              fillOpacity={0.85}
              strokeWidth={1}
              isAnimationActive={false}
            />
          ))}
        </AreaChart>
      </ChartContainer>
    </ChartCard>
  );
}

// ---------------------------------------------------------------------------
// 4. Bugs per model — grouped horizontal bars (bugs/1k + high-sev/1k)
// ---------------------------------------------------------------------------

const BUGS_CONFIG = {
  bugs: { label: 'Bugs / 1k LOC', color: 'hsl(220 75% 58%)' },
  highSev: { label: 'High-sev (≥7) / 1k', color: 'hsl(0 75% 58%)' },
} satisfies ChartConfig;

// Sorted ascending by overall flag rate (cleanest first). Source: §4 (merged-PR-only,
// mrge-io excluded).
const BUGS_DATA = [
  { model: 'GPT-5.5', bugs: 0.327, highSev: 0.086 },
  { model: 'Opus 4.7', bugs: 0.33, highSev: 0.098 },
  { model: 'Sonnet 4.6', bugs: 0.399, highSev: 0.139 },
  { model: 'GPT-5.4', bugs: 0.406, highSev: 0.175 },
  { model: 'Cursor Composer', bugs: 0.563, highSev: 0.16 },
  { model: 'Opus 4.6', bugs: 0.708, highSev: 0.24 },
  { model: 'GPT-5.3 Codex', bugs: 0.721, highSev: 0.207 },
  { model: 'Opus 4.8', bugs: 0.756, highSev: 0.191 },
];

function BugsTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: (typeof BUGS_DATA)[number] }>;
}) {
  if (!active || !payload?.length) {
    return null;
  }
  const row = payload[0]?.payload;
  if (!row) {
    return null;
  }
  return (
    <div className="rounded-lg border border-border/50 bg-background px-3 py-2 text-xs shadow-xl">
      <div className="mb-1.5 font-medium">{row.model}</div>
      <div className="grid gap-1">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: BUGS_CONFIG.bugs.color }}
            />
            <span className="text-muted-foreground">{BUGS_CONFIG.bugs.label}</span>
          </div>
          <span className="font-medium tabular-nums text-foreground">{row.bugs.toFixed(3)}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: BUGS_CONFIG.highSev.color }}
            />
            <span className="text-muted-foreground">{BUGS_CONFIG.highSev.label}</span>
          </div>
          <span className="font-medium tabular-nums text-foreground">{row.highSev.toFixed(3)}</span>
        </div>
      </div>
    </div>
  );
}

export function BugsPerModelChart() {
  return (
    <ChartCard
      title="Bugs per model (per 1k AI-authored lines)"
      legend={[
        { label: BUGS_CONFIG.bugs.label, color: BUGS_CONFIG.bugs.color },
        { label: BUGS_CONFIG.highSev.label, color: BUGS_CONFIG.highSev.color },
      ]}
    >
      <ChartContainer config={BUGS_CONFIG} className="h-96 w-full">
        <BarChart
          data={BUGS_DATA}
          layout="vertical"
          margin={{ top: 4, right: 16, left: 8, bottom: 0 }}
          barCategoryGap="22%"
        >
          <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke="hsl(240 5% 18%)" />
          <XAxis
            type="number"
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v: number) => v.toFixed(2)}
          />
          <YAxis
            type="category"
            dataKey="model"
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={118}
          />
          <ChartTooltip content={<BugsTooltip />} cursor={{ fill: 'hsl(240 5% 12%)' }} />
          <Bar
            dataKey="bugs"
            fill="var(--color-bugs)"
            radius={[0, 3, 3, 0]}
            isAnimationActive={false}
          />
          <Bar
            dataKey="highSev"
            fill="var(--color-highSev)"
            radius={[0, 3, 3, 0]}
            isAnimationActive={false}
          />
        </BarChart>
      </ChartContainer>
    </ChartCard>
  );
}

// ---------------------------------------------------------------------------
// 5. Median merged PRs per developer — vertical bars (monthly, 12mo)
// ---------------------------------------------------------------------------

const PR_CONFIG = {
  prs: { label: 'Median merged PRs / dev', color: 'hsl(220 75% 58%)' },
} satisfies ChartConfig;

// Median merged PRs per active developer, fixed installation cohort (installs already
// merging in May 2025), full months Jun 2025–May 2026. Source: §8 (GithubPR, merged-only).
const PR_DATA = [
  { label: "Jun '25", prs: 6 },
  { label: "Jul '25", prs: 7 },
  { label: "Aug '25", prs: 8 },
  { label: "Sep '25", prs: 8 },
  { label: "Oct '25", prs: 9 },
  { label: "Nov '25", prs: 9 },
  { label: "Dec '25", prs: 9 },
  { label: "Jan '26", prs: 10 },
  { label: "Feb '26", prs: 10 },
  { label: "Mar '26", prs: 11 },
  { label: "Apr '26", prs: 11 },
  { label: "May '26", prs: 12 },
];

function SimpleBarTooltip({
  active,
  payload,
  color,
  name,
  unit,
}: {
  active?: boolean;
  payload?: Array<{ value: number; payload: { label: string } }>;
  color: string;
  name: string;
  unit?: string;
}) {
  const item = payload?.[0];
  if (!active || !item) {
    return null;
  }
  return (
    <div className="rounded-lg border border-border/50 bg-background px-3 py-2 text-xs shadow-xl">
      <div className="mb-1.5 font-medium">{item.payload.label}</div>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
          <span className="text-muted-foreground">{name}</span>
        </div>
        <span className="font-medium tabular-nums text-foreground">
          {item.value}
          {unit}
        </span>
      </div>
    </div>
  );
}

export function PrsPerAuthorChart() {
  return (
    <ChartCard title="Median merged PRs per developer (monthly)">
      <ChartContainer config={PR_CONFIG} className="h-72 w-full">
        <BarChart data={PR_DATA} margin={{ top: 8, right: 20, left: 4, bottom: 0 }}>
          <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="hsl(240 5% 18%)" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={36} />
          <ChartTooltip
            content={
              <SimpleBarTooltip color={PR_CONFIG.prs.color} name="Median merged PRs / dev" />
            }
            cursor={{ fill: 'hsl(240 5% 12%)' }}
          />
          <Bar
            dataKey="prs"
            fill="var(--color-prs)"
            radius={[3, 3, 0, 0]}
            isAnimationActive={false}
          />
        </BarChart>
      </ChartContainer>
    </ChartCard>
  );
}

// ---------------------------------------------------------------------------
// 5b. LOC per developer — vertical bars (monthly, 12mo)
// ---------------------------------------------------------------------------

const LOC_DEV_CONFIG = {
  loc: { label: 'Median changed LOC / dev', color: 'hsl(220 75% 58%)' },
} satisfies ChartConfig;

// Median changed LOC (additions + deletions) per active developer, same fixed cohort,
// Jun 2025–May 2026. Source: addendum §A (GithubPR, merged-only).
const LOC_DEV_DATA = [
  { label: "Jun '25", loc: 1930 },
  { label: "Jul '25", loc: 1892 },
  { label: "Aug '25", loc: 2764 },
  { label: "Sep '25", loc: 2634 },
  { label: "Oct '25", loc: 3362 },
  { label: "Nov '25", loc: 3324 },
  { label: "Dec '25", loc: 3670 },
  { label: "Jan '26", loc: 3702 },
  { label: "Feb '26", loc: 4771 },
  { label: "Mar '26", loc: 4713 },
  { label: "Apr '26", loc: 5088 },
  { label: "May '26", loc: 5820 },
];

export function LocPerDevChart() {
  return (
    <ChartCard title="Median changed LOC per developer (monthly)">
      <ChartContainer config={LOC_DEV_CONFIG} className="h-72 w-full">
        <BarChart data={LOC_DEV_DATA} margin={{ top: 8, right: 20, left: 4, bottom: 0 }}>
          <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="hsl(240 5% 18%)" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
          <YAxis
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={48}
            tickFormatter={(v: number) => v.toLocaleString()}
          />
          <ChartTooltip
            content={
              <SimpleBarTooltip color={LOC_DEV_CONFIG.loc.color} name="Median changed LOC / dev" />
            }
            cursor={{ fill: 'hsl(240 5% 12%)' }}
          />
          <Bar
            dataKey="loc"
            fill="var(--color-loc)"
            radius={[3, 3, 0, 0]}
            isAnimationActive={false}
          />
        </BarChart>
      </ChartContainer>
    </ChartCard>
  );
}

// ---------------------------------------------------------------------------
// 5c. PR size (median lines changed) — vertical bars (monthly, 12mo)
// ---------------------------------------------------------------------------

const PR_SIZE_CONFIG = {
  size: { label: 'Median changed LOC / PR', color: 'hsl(220 75% 58%)' },
} satisfies ChartConfig;

// Median lines changed (additions + deletions) per merged PR, same fixed cohort,
// Jun 2025–May 2026. Source: addendum §B (GithubPR, merged-only).
const PR_SIZE_DATA = [
  { label: "Jun '25", size: 56 },
  { label: "Jul '25", size: 61 },
  { label: "Aug '25", size: 61 },
  { label: "Sep '25", size: 62 },
  { label: "Oct '25", size: 68 },
  { label: "Nov '25", size: 73 },
  { label: "Dec '25", size: 76 },
  { label: "Jan '26", size: 84 },
  { label: "Feb '26", size: 92 },
  { label: "Mar '26", size: 92 },
  { label: "Apr '26", size: 107 },
  { label: "May '26", size: 124 },
];

export function PrSizeChart() {
  return (
    <ChartCard title="PR size — median lines changed (monthly)">
      <ChartContainer config={PR_SIZE_CONFIG} className="h-72 w-full">
        <BarChart data={PR_SIZE_DATA} margin={{ top: 8, right: 20, left: 4, bottom: 0 }}>
          <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="hsl(240 5% 18%)" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={36} />
          <ChartTooltip
            content={
              <SimpleBarTooltip color={PR_SIZE_CONFIG.size.color} name="Median changed LOC / PR" />
            }
            cursor={{ fill: 'hsl(240 5% 12%)' }}
          />
          <Bar
            dataKey="size"
            fill="var(--color-size)"
            radius={[3, 3, 0, 0]}
            isAnimationActive={false}
          />
        </BarChart>
      </ChartContainer>
    </ChartCard>
  );
}

// MedianTtmChart removed — data was excluded from the published report.
